import { useEffect, useRef } from 'react'
import flatpickr from 'flatpickr'
import { Calendar } from 'lucide-react'
import { format, parseISO, isValid } from 'date-fns'
import { cn } from '@shared/lib/utils'
import { FieldLabel } from './field-label'
import { INPUT_CLASSES } from './form-styles'
import { dateOnlyToFlatpickrISO } from '@/shared/lib/date-helpers'
import { FORM_PLACEHOLDERS } from '@/shared/constants/form-messages'
import { createFlatpickrDateOfBirthOptions } from './flatpickr-date-of-birth-options'

import 'flatpickr/dist/flatpickr.min.css'

const DOB_MAX = format(new Date(), 'yyyy-MM-dd')

export interface DateOfBirthFieldProps {
  label: string
  required?: boolean
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  wrapperClassName?: string
  disabled?: boolean
  maxDate?: string
  positionElement?: HTMLElement | null
  position?: 'above' | 'below' | 'auto'
}

export function DateOfBirthField({
  label,
  required,
  value,
  onChange,
  placeholder = FORM_PLACEHOLDERS.DATE_OF_BIRTH,
  className,
  wrapperClassName,
  disabled = false,
  maxDate = DOB_MAX,
  positionElement,
  position = 'auto',
}: DateOfBirthFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const fpRef = useRef<flatpickr.Instance | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (!inputRef.current) return

    fpRef.current = flatpickr(inputRef.current, {
      ...createFlatpickrDateOfBirthOptions({
        defaultDateISO: undefined,
        onDateChange: (v) => onChangeRef.current(v),
        maxDate: maxDate ?? 'today',
      }),
      ...(positionElement && { positionElement }),
      position,
    })

    return () => {
      fpRef.current?.destroy()
      fpRef.current = null
    }
  }, [maxDate, positionElement, position])

  useEffect(() => {
    if (!fpRef.current) return
    const trimmed = (value ?? '').trim()
    if (!trimmed) {
      fpRef.current.clear()
      return
    }

    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
      ? trimmed
      : (() => {
          try {
            const d = parseISO(trimmed)
            return isValid(d) ? format(d, 'yyyy-MM-dd') : ''
          } catch {
            return ''
          }
        })()
    if (normalized) {
      const dateToSet = dateOnlyToFlatpickrISO(normalized) ?? normalized
      fpRef.current.setDate(dateToSet, false)
    } else {
      fpRef.current.clear()
    }
  }, [value])

  const openPicker = () => {
    if (!disabled) fpRef.current?.open()
  }

  return (
    <div className={cn('space-y-2', className)}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <div
        className={cn(
          'relative flex items-center justify-between',
          INPUT_CLASSES,
          '!px-0',
          'focus-within:ring-1 focus-within:ring-client-primary focus-within:ring-offset-0',
          wrapperClassName
        )}
      >
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          disabled={disabled}
          className="min-w-0 flex-1 border-0 bg-transparent !px-4 !py-0 text-sm text-client-neutral-950 placeholder:text-client-neutral-500 focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0 disabled:cursor-not-allowed"
          data-input
        />
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-client-neutral-950 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
          aria-label="Open calendar"
        >
          <Calendar className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}
